import { Card } from '@/components/ui/card';
import { SOL_TOKEN_ADDRESS } from '@/config';
import { formatCryptoPrice, formatDate, formatValueWithCompactNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { limitedOrder } from '@/page/trade/hook/useTokenOrder';
import { useAppStore } from '@/store';
import TokenAvatar from '@/widget/TokenAvatar';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  order: limitedOrder;
}

export default (props: IProps) => {
  const { order } = props;
  const { lang, chainInfo } = useAppStore();
  const { t } = useTranslation();
  const { transactions } = useBoomWallet();

  const type = order.inputMint.toLocaleLowerCase() === SOL_TOKEN_ADDRESS.toLocaleLowerCase() ? 'buy' : 'sell';

  const tradeAmount = useMemo(() => {
    if (order.transformAmount) {
      if (type == 'buy') {
        return BigNumber(order.takingAmount)
          .div(10 ** order.outputDecimals)
          .toFixed();
      } else {
        return BigNumber(order.makingAmount)
          .div(10 ** order.inputDecimals)
          .toFixed();
      }
    } else {
      return type == 'buy' ? order.takingAmount : order.makingAmount;
    }
  }, [order, type]);

  const thatTimePrice2usd = useMemo(() => BigNumber(order.tokenRate).times(order.solPrice).toFixed(), [order.tokenRate, order.solPrice]);

  const targetPrice2Sol = useMemo(() => {
    if (order.transformAmount) {
      if (type == 'buy') {
        return BigNumber(order.makingAmount)
          .div(10 ** order.inputDecimals)
          .div(tradeAmount)
          .toFixed();
      } else {
        return BigNumber(order.takingAmount)
          .div(10 ** order.outputDecimals)
          .div(tradeAmount)
          .toFixed();
      }
    } else {
      if (type == 'buy') {
        return BigNumber(order.makingAmount).div(order.takingAmount).toFixed();
      } else {
        return BigNumber(order.takingAmount).div(order.makingAmount).toFixed();
      }
    }
  }, [order, type, tradeAmount]);

  const targetPrice2usd = useMemo(() => {
    if (order.transformAmount) {
      if (type == 'buy') {
        return BigNumber(order.makingAmount)
          .div(10 ** order.inputDecimals)
          .times(order.solPrice)
          .div(tradeAmount)
          .toFixed();
      } else {
        return BigNumber(order.takingAmount)
          .div(10 ** order.outputDecimals)
          .times(order.solPrice)
          .div(tradeAmount)
          .toFixed();
      }
    } else {
      if (type == 'buy') {
        return BigNumber(order.makingAmount).times(order.solPrice).div(tradeAmount).toFixed();
      } else {
        return BigNumber(order.takingAmount).times(order.solPrice).div(tradeAmount).toFixed();
      }
    }
  }, [targetPrice2Sol, tradeAmount, order]);

  const limitChg = useMemo(
    () => BigNumber(thatTimePrice2usd).minus(targetPrice2usd).div(thatTimePrice2usd).times(100).abs().toFixed(2),
    [targetPrice2usd, thatTimePrice2usd],
  );

  const OrderCap = useMemo(() => {
    const colorClass = type == 'buy' ? 'text-buyColor bg-[#1FB9831F]' : 'text-sellColor bg-[#FF5F5F1A]';
    const label = type == 'buy' ? t('Trade.buy') : t('Trade.sell');
    const limitOrderToken =
      type == 'buy' ? { address: order.outputMint, symbol: order.outputSymbol } : { address: order.inputMint, symbol: order.inputSymbol };
    return () => (
      <div className="flex items-center gap-x-[4px]">
        <div className={cn('px-[6px] py-0 rounded-[4px] flex-shrink-0 flex items-center', colorClass)}>{label}</div>
        <TokenAvatar className="w-[24px] h-[24px]" logo="" address={limitOrderToken?.address} />
        <span className="text-[14px] flex-shrink-0">{limitOrderToken.symbol}</span>
      </div>
    );
  }, [order, type, lang]);

  const cancelOrder = async () => {
    await transactions.cancelLimitOrder({ orders: [order.orderNo] });
  };

  return (
    <Card className="p-[12px] rounded-[8px] text-[12px] shadow-none">
      <div className="flex justify-between items-center">
        <OrderCap />
        {order.status == 'Active' && (
          <div className="text-[#45A3FF] underline text-[14px] font-[500]" onClick={cancelOrder}>
            {t('Trade.cancel')}
          </div>
        )}
      </div>
      <div className="py-[6px] flex justify-between">
        <span className="text-secondaryTextColor">{t('Trade.amount')}</span>
        <span>{formatValueWithCompactNumber(tradeAmount)}</span>
      </div>
      <div className="py-[6px] flex justify-between">
        <span className="text-secondaryTextColor">{t('Trade.condition')}</span>
        <div>
          {type == 'buy' ? t('Trade.priceDrop') : t('Trade.priceIncrease')}
          <span className={type == 'buy' ? 'text-sellColor' : 'text-buyColor'}>≥{limitChg}%</span>
        </div>
      </div>
      <div className="py-[6px] flex justify-between">
        <span className="text-secondaryTextColor">{t('Trade.createWhen')}</span>
        <div>
          {formatCryptoPrice(order.tokenRate)}{' '}
          <span className="text-secondaryTextColor">
            {chainInfo.symbol} (${formatCryptoPrice(thatTimePrice2usd)})
          </span>
        </div>
      </div>
      <div className="py-[6px] flex justify-between">
        <span className="text-secondaryTextColor">{t('Trade.targetPrice')}</span>
        <div>
          {formatCryptoPrice(targetPrice2Sol)}{' '}
          <span className="text-secondaryTextColor">
            {chainInfo.symbol} (${formatCryptoPrice(targetPrice2usd)})
          </span>
        </div>
      </div>
      {order.status == 'Active' && (
        <div className="py-[6px] flex justify-between">
          <span className="text-secondaryTextColor">{t('Trade.expiration')}</span>
          <span className="text-secondaryTextColor">{order.expiredAt == null ? t('Trade.never') : formatDate(order.expiredAt)}</span>
        </div>
      )}
      {order.status !== 'Active' && (
        <div className="py-[6px] flex justify-between">
          <span className="text-secondaryTextColor">{t('Trade.status')}</span>
          <span className={cn('text-[12px] font-[400]', order.status == 'Completed' ? 'text-buyColor' : 'text-sellColor')}>
            {order.status == 'Completed' ? t('Trade.success') : t('Trade.canceled')}
          </span>
        </div>
      )}
    </Card>
  );
};
